import { useCallback, useMemo, useState, type FormEvent } from "react";
import { AlertTriangle, KeyRound, Plus, Search, ShieldCheck, Trash2, UserRoundCog, UsersRound } from "lucide-react";
import { PLATFORM_ADMIN_PASSWORD_MIN_LENGTH, type PlatformRole, type SessionView } from "@control-plane/contracts";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { businessLabel } from "../lib/displayNames";
import { PageHeader } from "../components/PageHeader";
import { Dialog } from "../components/Overlay";
import { V3Select } from "../components/V3Select";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { RemotePageState } from "../components/RemotePageState";
import { AiExplainError, AiRiskCheck } from "../components/AssistantInlineHelp";
import { adminsCapabilities } from "../lib/pageCapabilities";

type AdminDirectoryItem = {
  id?: string;
  selectionToken?: string;
  approvalSelectionToken?: string;
  email: string;
  displayName: string;
  role: PlatformRole;
  roleLabel?: string;
  status: string;
  statusLabel?: string;
  lastLoginAt?: string | null;
  lastLoginLabel?: string | null;
  createdAt?: string | null;
  capabilitySummary?: string[] | string | null;
  capabilities?: string[] | null;
  mfa?: { enabled?: boolean; label?: string } | null;
  riskLabel?: string | null;
  isCurrentOperator?: boolean;
  disabledReason?: string | null;
};

type AdminDirectoryResponse = {
  items: AdminDirectoryItem[];
  pageInfo?: { pageSize?: number; hasNextPage?: boolean; total?: number };
};

type AdminView = {
  key: string;
  email: string;
  displayName: string;
  role: PlatformRole;
  roleLabel: string;
  status: string;
  statusLabel: string;
  mfaLabel: string;
  riskLabel: string;
  lastLoginText: string;
  createdText: string;
  capabilities: string[];
  isCurrentOperator: boolean;
  disabledReason: string;
  approvalSelectionToken: string;
};
type AdminApprovalOperation = "admin.create" | "admin.update" | "admin.delete";
type AdminApprovalOperationItem = { operationType?: string; disabled?: boolean; unavailableReason?: string; label?: string };
type AdminApprovalTarget = { selectionToken?: string; label?: string; disabled?: boolean; unavailableReason?: string };
type AdminApprovalPreview = { impact?: string[]; recovery?: string; canonicalBusinessInputs?: Record<string, unknown>; idempotencyKey?: string };

const roleLabels: Record<PlatformRole, string> = {
  auditor: "只读审计员",
  support_operator: "代管操作员",
  tenant_operator: "租户运营员",
  billing_operator: "商业运营员",
  security_admin: "安全管理员",
  platform_owner: "平台负责人",
};

const roleCapabilitySummary: Record<PlatformRole, string[]> = {
  auditor: ["查看审计链", "导出脱敏证据"],
  support_operator: ["申请受控代管", "激活本人被批准的代管"],
  tenant_operator: ["查看企业资料", "处理企业接入事项"],
  billing_operator: ["查看订购与账单", "准备商业变更"],
  security_admin: ["管理安全策略", "复核高风险申请"],
  platform_owner: ["最终治理责任人", "可回收平台管理员"],
};

const roleOptions: Array<{ value: PlatformRole; label: string; hint: string }> = [
  { value: "auditor", label: roleLabels.auditor, hint: "只看证据，不做写操作" },
  { value: "support_operator", label: roleLabels.support_operator, hint: "短时代管，不常驻企业" },
  { value: "tenant_operator", label: roleLabels.tenant_operator, hint: "企业接入与日常运营" },
  { value: "billing_operator", label: roleLabels.billing_operator, hint: "订购、账单与用量核对" },
  { value: "security_admin", label: roleLabels.security_admin, hint: "安全复核与高风险审批" },
  { value: "platform_owner", label: roleLabels.platform_owner, hint: "平台最终负责人" },
];

const statusOptions = [
  { value: "active", label: "启用" },
  { value: "suspended", label: "停用" },
] as const;
const mfaOptions = [
  { value: "all", label: "全部 MFA" },
  { value: "enabled", label: "已启用 MFA" },
  { value: "disabled", label: "未启用 MFA" },
] as const;
const lastLoginOptions = [
  { value: "all", label: "全部登录时间" },
  { value: "recent", label: "近期登录" },
  { value: "never", label: "尚未登录" },
] as const;
const riskOptions = [
  { value: "all", label: "全部风险" },
  { value: "high", label: "高风险角色" },
  { value: "medium", label: "代管受控" },
  { value: "low", label: "只读审计" },
] as const;

type AdminCreateForm = { email: string; displayName: string; password: string; role: PlatformRole; reason: string; deliveryMode: "invite" | "temporary_password" };
type AdminUpdateForm = { role: PlatformRole; status: "active" | "suspended"; reason: string };

function formatTime(value?: string | null, mode: "datetime" | "date" = "datetime") {
  if (!value) return mode === "datetime" ? "从未登录" : "尚无记录";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间待确认";
  return mode === "datetime" ? date.toLocaleString("zh-CN") : date.toLocaleDateString("zh-CN");
}

function createRequestKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeAdmin(item: AdminDirectoryItem, currentEmail: string): AdminView {
  const role = item.role;
  const capabilities = Array.isArray(item.capabilitySummary)
    ? item.capabilitySummary.filter(Boolean)
    : Array.isArray(item.capabilities)
      ? item.capabilities.filter(Boolean)
    : typeof item.capabilitySummary === "string" && item.capabilitySummary.trim()
      ? [item.capabilitySummary.trim()]
      : roleCapabilitySummary[role] ?? ["按当前角色范围控制"];
  return {
    key: item.id ?? item.email,
    email: item.email,
    displayName: item.displayName,
    role,
    roleLabel: item.roleLabel ?? businessLabel(roleLabels as Record<string, string>, role, "角色待确认"),
    status: item.status,
    statusLabel: item.statusLabel ?? (item.status === "active" ? "启用" : item.status === "suspended" ? "停用" : "状态待确认"),
    mfaLabel: item.mfa?.label ?? (item.mfa?.enabled ? "已启用 MFA" : "MFA 待确认"),
    riskLabel: item.riskLabel ?? (role === "platform_owner" || role === "security_admin" ? "高风险角色" : "常规角色"),
    lastLoginText: item.lastLoginLabel ?? formatTime(item.lastLoginAt),
    createdText: formatTime(item.createdAt, "date"),
    capabilities,
    isCurrentOperator: Boolean(item.isCurrentOperator) || item.email === currentEmail,
    disabledReason: item.disabledReason ?? "",
    approvalSelectionToken: item.selectionToken ?? item.approvalSelectionToken ?? "",
  };
}

function adminQueryPath(filters: { q: string; role: string; status: string; mfa: string; lastLogin: string; risk: string }) {
  const params = new URLSearchParams();
  const trimmed = filters.q.trim();
  if (trimmed) params.set("q", trimmed);
  if (filters.role !== "all") params.set("role", filters.role);
  if (filters.status !== "all") params.set("status", filters.status);
  if (filters.mfa !== "all") params.set("mfa", filters.mfa);
  if (filters.lastLogin !== "all") params.set("lastLogin", filters.lastLogin);
  if (filters.risk !== "all") params.set("risk", filters.risk);
  params.set("pageSize", "50");
  return `/api/admins?${params.toString()}`;
}

function approvalTargetFrom(response: { items?: AdminApprovalTarget[] }, fallbackMessage: string) {
  const option = (response.items ?? []).find((item) => item.selectionToken && !item.disabled);
  if (!option?.selectionToken) throw new Error(fallbackMessage);
  return option.selectionToken;
}

async function adminApprovalTarget(operationType: AdminApprovalOperation, fallbackMessage: string, query?: Record<string, string>) {
  const params = new URLSearchParams({ operationType });
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value.trim()) params.set(key, value.trim());
  }
  const response = await api<{ items?: AdminApprovalTarget[] }>(`/api/enterprise/approval-targets?${params.toString()}`);
  return approvalTargetFrom(response, fallbackMessage);
}

async function ensureAdminApprovalOperation(operationType: AdminApprovalOperation) {
  const response = await api<{ items?: AdminApprovalOperationItem[] }>("/api/enterprise/approval-operations");
  const operation = (response.items ?? []).find((item) => item.operationType === operationType);
  if (!operation) throw new Error("平台审批目录暂未开放该管理员动作，请刷新后重试。");
  if (operation.disabled) throw new Error(operation.unavailableReason || "该管理员动作当前暂不可申请。");
}

async function requestAdminApproval(input: {
  operationType: AdminApprovalOperation;
  target: Record<string, unknown>;
  payload: Record<string, unknown>;
  reason: string;
  idempotencyKey: string;
}) {
  await ensureAdminApprovalOperation(input.operationType);
  const preview = await api<AdminApprovalPreview>("/api/enterprise/approvals/preview", {
    method: "POST",
    body: JSON.stringify({
      operationType: input.operationType,
      target: input.target,
      payload: input.payload,
    }),
  });
  await api("/api/enterprise/approvals", {
    method: "POST",
    body: JSON.stringify({
      operationType: input.operationType,
      target: input.target,
      reason: input.reason,
      payload: preview.canonicalBusinessInputs ?? input.payload,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      idempotencyKey: preview.idempotencyKey ?? input.idempotencyKey,
    }),
  });
}

export function AdminsPage({ session }: { session: SessionView }) {
  const caps = adminsCapabilities(session.actions, session.admin.role);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mfaFilter, setMfaFilter] = useState("all");
  const [lastLoginFilter, setLastLoginFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [updateTarget, setUpdateTarget] = useState<AdminView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminView | null>(null);
  const [feedback, setFeedback] = useState("");

  const loader = useCallback(async () => {
    const data = await api<AdminDirectoryResponse>(adminQueryPath({ q, role: roleFilter, status: statusFilter, mfa: mfaFilter, lastLogin: lastLoginFilter, risk: riskFilter }));
    return {
      rows: data.items.map((item) => normalizeAdmin(item, session.admin.email)),
      pageInfo: data.pageInfo,
    };
  }, [lastLoginFilter, mfaFilter, q, riskFilter, roleFilter, session.admin.email, statusFilter]);
  const remote = useRemote(loader, (value) => value.rows.length === 0);

  const columns = useMemo<Array<DataTableColumn<AdminView>>>(() => [
    {
      key: "person",
      header: "管理员",
      width: "28%",
      render: (item) => <span className="security-person-cell">
        <strong>{item.displayName}</strong>
        <small>{item.email}</small>
      </span>,
    },
    {
      key: "role",
      header: "平台角色",
      width: "18%",
      render: (item) => <span className="security-person-cell">
        <strong>{item.roleLabel}</strong>
        <small>{item.capabilities.slice(0, 2).join(" / ")}</small>
        <small>{item.riskLabel}</small>
      </span>,
    },
    {
      key: "status",
      header: "状态",
      width: "14%",
      render: (item) => <span className="security-person-cell">
        <StatusBadge value={item.status} />
        <small>{item.statusLabel}</small>
        <small>{item.mfaLabel}</small>
      </span>,
    },
    { key: "last", header: "最近登录", width: "17%", render: (item) => <span className="app-number">{item.lastLoginText}</span> },
    { key: "created", header: "创建时间", width: "13%", render: (item) => <span className="app-number">{item.createdText}</span> },
  ], []);

  const filters = <div className="ui-ledger-filters security-ledger-filters">
    <label className="ui-ledger-filter-primary">
      <span className="sr-only">搜索管理员</span>
      <Search size={15} aria-hidden />
      <input className="ui-inline-field" aria-label="搜索管理员姓名或邮箱" placeholder="姓名 / 邮箱" value={q} onChange={(event) => setQ(event.target.value)} />
    </label>
    <label className="security-compact-filter">
      <span>按角色筛选</span>
      <V3Select
        value={roleFilter}
        onChange={setRoleFilter}
        ariaLabel="按角色筛选"
        options={[{ value: "all", label: "全部角色" }, ...roleOptions.map((role) => ({ value: role.value, label: role.label, hint: role.hint }))]}
      />
    </label>
    <label className="security-compact-filter">
      <span>按状态筛选</span>
      <V3Select
        value={statusFilter}
        onChange={setStatusFilter}
        ariaLabel="按状态筛选"
        options={[{ value: "all", label: "全部状态" }, ...statusOptions]}
      />
    </label>
    <label className="security-compact-filter">
      <span>按 MFA 筛选</span>
      <V3Select value={mfaFilter} onChange={setMfaFilter} ariaLabel="按 MFA 筛选" options={[...mfaOptions]} />
    </label>
    <label className="security-compact-filter">
      <span>按最后登录筛选</span>
      <V3Select value={lastLoginFilter} onChange={setLastLoginFilter} ariaLabel="按最后登录筛选" options={[...lastLoginOptions]} />
    </label>
    <label className="security-compact-filter">
      <span>按风险筛选</span>
      <V3Select value={riskFilter} onChange={setRiskFilter} ariaLabel="按风险筛选" options={[...riskOptions]} />
    </label>
  </div>;

  async function createAdmin(form: AdminCreateForm, requestKey: string) {
    setFeedback("");
    await requestAdminApproval({
      operationType: "admin.create",
      target: { adminEmail: form.email },
      payload: { email: form.email, displayName: form.displayName, password: form.password, role: form.role, reason: form.reason, deliveryMode: form.deliveryMode },
      reason: form.reason,
    idempotencyKey: requestKey,
  });
    setFeedback("审批申请已提交。请另一位有权限的管理员在审批中心批准，批准后再执行生效。");
    setShowCreate(false);
    await remote.reload();
  }

  async function updateAdmin(item: AdminView, form: AdminUpdateForm, requestKey: string) {
    setFeedback("");
    const selectionToken = item.approvalSelectionToken || await adminApprovalTarget("admin.update", "平台尚未返回该管理员的审批对象，请刷新后重试。", { email: item.email });
    await requestAdminApproval({
      operationType: "admin.update",
      target: { selectionToken },
      payload: { role: form.role, status: form.status, reason: form.reason },
      reason: form.reason,
      idempotencyKey: requestKey,
    });
    setFeedback("变更审批申请已提交，批准后再由明确执行入口生效。");
    setUpdateTarget(null);
    await remote.reload();
  }

  async function deleteAdmin(item: AdminView, requestKey: string, reason: string) {
    if (item.isCurrentOperator) {
      setFeedback("不能删除当前登录账号，请先使用另一位平台负责人账号处理。");
      setDeleteTarget(null);
      return;
    }
    const selectionToken = item.approvalSelectionToken || await adminApprovalTarget("admin.delete", "平台尚未返回该管理员的审批对象，请刷新后重试。", { email: item.email });
    await requestAdminApproval({
      operationType: "admin.delete",
      target: { selectionToken },
      payload: { reason },
      reason,
      idempotencyKey: requestKey,
    });
    setFeedback("删除审批申请已提交；批准并执行后会吊销该管理员的全部会话。");
    setDeleteTarget(null);
    await remote.reload();
  }

  return <div className="page">
    <PageHeader
      icon={UserRoundCog}
      title="平台管理员"
      meta={<span>角色能力按最小权限授予</span>}
      actions={caps.showCreate ? <button className="button primary" onClick={() => setShowCreate(true)}><Plus size={17} />本页新增管理员</button> : null}
    />
    {feedback && <div className="success-message security-feedback"><ShieldCheck size={16} aria-hidden />{feedback}</div>}
    <SectionCard
      icon={UsersRound}
      title="管理员目录"
      meta={remote.state.status === "ready" || remote.state.status === "empty" ? `${remote.state.data.rows.length} 位` : "正在读取"}
      variant="ledger"
      filters={filters}
    >
      <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="当前筛选条件下没有管理员。">
        {(data) => <DataTable
          ariaLabel="平台管理员目录"
          rows={data.rows}
          columns={columns}
          rowKey={(item) => item.key}
          rowActions={(item) => <div className="inline-actions">
            {caps.showUpdate && <button type="button" className="button ghost" onClick={() => setUpdateTarget(item)}>管理</button>}
            {caps.showDelete && <button
              type="button"
              className="button ghost danger"
              disabled={item.isCurrentOperator}
              title={item.isCurrentOperator ? "不能删除当前登录账号" : "删除会吊销该管理员的全部会话"}
              onClick={() => setDeleteTarget(item)}
            ><Trash2 size={15} />删除</button>}
          </div>}
          empty="当前没有平台管理员。"
        />}
      </RemotePageState>
    </SectionCard>
    <SectionCard
      icon={KeyRound}
      title="角色能力差异"
      description="先按职责选择最小角色；扩大权限、停用和删除均需要明确原因，并以平台核验结果为准。"
    >
      <div className="security-role-grid">
        {roleOptions.map((role) => <article key={role.value} className="security-role-card">
          <strong>{role.label}</strong>
          <span>{role.hint}</span>
          <small>{roleCapabilitySummary[role.value].join(" / ")}</small>
        </article>)}
      </div>
      <div className="security-risk-note" role="note"><AlertTriangle size={16} aria-hidden />删除会吊销该管理员的全部会话；历史审计记录继续保留。</div>
    </SectionCard>
    {showCreate && <CreateAdminDialog onClose={() => setShowCreate(false)} onSubmit={createAdmin} />}
    {updateTarget && <UpdateAdminDialog item={updateTarget} onClose={() => setUpdateTarget(null)} onSubmit={updateAdmin} />}
    {deleteTarget && <DeleteAdminDialog item={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={deleteAdmin} />}
  </div>;
}

function CreateAdminDialog({ onClose, onSubmit }: { onClose: () => void; onSubmit: (form: AdminCreateForm, requestKey: string) => Promise<void> }) {
  const [form, setForm] = useState<AdminCreateForm>({ email: "", displayName: "", password: "", role: "auditor", reason: "", deliveryMode: "invite" });
  const [requestKey] = useState(() => createRequestKey("admin-create"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email) { setError("请填写管理员邮箱。"); return; }
    if (form.reason.trim().length < 8) { setError("请填写新增原因，平台不会代替你生成申请理由。"); return; }
    setBusy(true);
    setError("");
    try {
      await onSubmit({ ...form, email, displayName: form.displayName.trim() || email, reason: form.reason.trim() }, requestKey);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "新增申请提交失败，表单内容已保留。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title="本页新增平台管理员" description="单页填写身份、角色与原因；审批通过后才会生效。" onClose={onClose} wide>
    <form className="form-grid" onSubmit={submit}>
      <label>显示名称<input name="displayName" aria-label="姓名" placeholder="姓名" value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} /></label>
      <label>邮箱<input name="email" aria-label="邮箱" type="email" placeholder="邮箱" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required /></label>
      <label>平台角色<V3Select value={form.role} onChange={(value) => setForm({ ...form, role: value as PlatformRole })} options={roleOptions} ariaLabel="平台角色" /></label>
      <fieldset className="security-choice-group full">
        <legend>角色能力</legend>
        {roleOptions.map((role) => <button
          key={role.value}
          type="button"
          className={form.role === role.value ? "button secondary active" : "button ghost"}
          onClick={() => setForm({ ...form, role: role.value })}
        >
          {role.label}
        </button>)}
      </fieldset>
      <fieldset className="security-choice-group">
        <legend>开通方式</legend>
        <button type="button" className={form.deliveryMode === "invite" ? "button secondary active" : "button ghost"} onClick={() => setForm({ ...form, deliveryMode: "invite" })}>发送邀请</button>
        <button type="button" className={form.deliveryMode === "temporary_password" ? "button secondary active" : "button ghost"} onClick={() => setForm({ ...form, deliveryMode: "temporary_password" })}>临时密码</button>
      </fieldset>
      <label>临时密码<input name="temporaryPassword" aria-label="临时密码" placeholder="临时密码" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} minLength={PLATFORM_ADMIN_PASSWORD_MIN_LENGTH} pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{10,}" title="至少 10 位，并同时包含大写字母、小写字母和数字" autoComplete="new-password" /><small className="field-hint">如选择发送邀请，可留给审批流程补充；填写时至少 10 位，并同时包含大写字母、小写字母和数字。</small></label>
      <label className="full">新增原因<textarea name="reason" aria-label="原因" placeholder="原因" rows={3} minLength={8} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} required /></label>
      <div className="security-risk-note full" role="note"><ShieldCheck size={16} aria-hidden />角色能力差异：{roleCapabilitySummary[form.role].join(" / ")}。</div>
      {error && <div className="form-error full inline-feedback"><span>{error}</span><AiExplainError message={error} context="本页新增平台管理员" /></div>}
      <div className="form-actions full">
        <AiRiskCheck action="新增平台管理员" detail={`准备新增 ${form.displayName || "未填写姓名"}，角色 ${businessLabel(roleLabels as Record<string, string>, form.role, "角色待确认")}。审批通过前不会生效。`} />
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy}><ShieldCheck size={16} />{busy ? "正在提交…" : "提交新增审批"}</button>
      </div>
    </form>
  </Dialog>;
}

function UpdateAdminDialog({ item, onClose, onSubmit }: { item: AdminView; onClose: () => void; onSubmit: (item: AdminView, form: AdminUpdateForm, requestKey: string) => Promise<void> }) {
  const [form, setForm] = useState<AdminUpdateForm>({ role: item.role, status: item.status === "suspended" ? "suspended" : "active", reason: "" });
  const [requestKey] = useState(() => createRequestKey("admin-update"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const currentCapabilities = roleCapabilitySummary[item.role] ?? [];
  const nextCapabilities = roleCapabilitySummary[form.role] ?? [];
  const gained = nextCapabilities.filter((capability) => !currentCapabilities.includes(capability));
  const lost = currentCapabilities.filter((capability) => !nextCapabilities.includes(capability));
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (form.reason.trim().length < 8) { setError("请补充变更原因。"); return; }
    setBusy(true);
    setError("");
    try {
      await onSubmit(item, { ...form, reason: form.reason.trim() }, requestKey);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "变更申请提交失败，表单内容已保留。");
    } finally {
      setBusy(false);
    }
  }
  return <Dialog title={`管理 ${item.displayName}`} description="变更角色或状态前，请确认职责范围；结果以平台核验为准。" onClose={onClose}>
    <form className="form-grid" onSubmit={submit}>
      <label>平台角色<V3Select value={form.role} onChange={(value) => setForm({ ...form, role: value as PlatformRole })} options={roleOptions} ariaLabel="平台角色" /></label>
      <label>账号状态<V3Select value={form.status} onChange={(value) => setForm({ ...form, status: value as "active" | "suspended" })} options={[...statusOptions]} ariaLabel="账号状态" /></label>
      <label className="full">变更原因<textarea name="reason" aria-label="原因" placeholder="原因" rows={3} minLength={8} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} required /></label>
      <div className="security-risk-note full" role="note"><ShieldCheck size={16} aria-hidden />角色能力差异：新增 {gained.length ? gained.join(" / ") : "无"}；失去 {lost.length ? lost.join(" / ") : "无"}。</div>
      {error && <div className="form-error full inline-feedback"><span>{error}</span><AiExplainError message={error} context="管理平台管理员" /></div>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy}>{busy ? "正在提交…" : "提交审批"}</button>
      </div>
    </form>
  </Dialog>;
}

function DeleteAdminDialog({ item, onClose, onConfirm }: { item: AdminView; onClose: () => void; onConfirm: (item: AdminView, requestKey: string, reason: string) => Promise<void> }) {
  const [requestKey] = useState(() => createRequestKey("admin-delete"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
   async function confirmDelete() {
    if (reason.trim().length < 8) { setError("请填写删除原因，平台不会代替你生成申请理由。"); return; }
    setBusy(true);
    setError("");
    try {
      await onConfirm(item, requestKey, reason.trim());
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "删除申请提交失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }
  return <ConfirmDialog
    title="删除平台管理员"
    description={`确认删除 ${item.displayName}？`}
    confirmLabel="提交删除审批"
    tone="danger"
    busy={busy}
     onConfirm={() => void confirmDelete()}
    onClose={onClose}
    confirmDisabled={item.isCurrentOperator}
  >
    <p>删除会吊销该管理员的全部会话；历史审计记录继续保留。当前登录账号不可删除。</p>
    <label className="full">删除原因<textarea name="reason" aria-label="原因" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="请说明离任、职责调整或风险处置背景" /></label>
    {error && <div className="form-error" role="alert">{error}</div>}
  </ConfirmDialog>;
}
